import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  GetPatientsLabDetailResponse,
  OrderRequestsListRequest,
} from '@/types/scheduling-api';

/**
 * POST /api/Patients/GetPatientLabDetails
 *
 * Lists Phlebotomist-visible lab orders for the practice. Mirrors
 * Flutter `getPatientLabDetails` (`scheduling_data_source_impl.dart:
 * 1158-1174`). Drives the Phlebotomist Order Requests tab.
 *
 * Wire verified against the 2026-05-21 dev curl (Andrew — PR #295 §1
 * fixup). Request body matches `OrderRequestsListRequest` exactly.
 * Response matches `GetPatientsLabDetailResponse` except for 3
 * additional fields per row that Flutter's `fromJson` doesn't read
 * but the wire ships: `notePlainText`, `patientDocumentId`,
 * `labDetails`. All 3 are now on the TS interface (see
 * `PatientLabOrderDetail` doc-comment).
 *
 * `useQuery` over POST because the response is deterministic against
 * the body — same pattern as `useFollowUpRequestList`. AbortSignal
 * forwarded so React-Query auto-cancels on filter changes.
 *
 * PROD-2653: `labVendorId` (optional) narrows the list to one lab vendor.
 * It rides in the POST body with the rest of the filter and is part of
 * the query key, so switching vendor refetches.
 *
 * SECURITY — backend role enforcement (PR #295 Samer review §3,
 * Andrew confirmation 2026-05-21):
 *   **The backend does NOT enforce role.** The curl Andrew captured
 *   came from `roleid: 2` (Practitioner) and succeeded — a
 *   non-Phlebotomist can list lab details directly. Client gate
 *   (`tenantRoleId === '19'` + `VITE_LAB_LINKAGE_ENABLED`) is the
 *   ONLY mechanism keeping non-Phlebotomists out, and it's
 *   bypassable by a direct API call.
 */

interface LabOrdersListVariables {
  filter: OrderRequestsListRequest;
}

export function useLabOrdersList(
  variables: LabOrdersListVariables,
): UseQueryResult<GetPatientsLabDetailResponse, Error> {
  const { filter } = variables;
  return useQuery<GetPatientsLabDetailResponse, Error>({
    queryKey: [
      'lab-orders',
      filter.organizationId ?? '',
      filter.patientId ?? '',
      filter.providerId ?? '',
      filter.search ?? '',
      filter.status ?? '',
      filter.pageNumber ?? 1,
      filter.pageSize ?? 10,
      filter.fromDate ?? '',
      filter.toDate ?? '',
      filter.labVendorId ?? '',
    ],
    queryFn: ({ signal }) =>
      post<GetPatientsLabDetailResponse>(
        '/api/Patients/GetPatientLabDetails',
        filter,
        { signal },
      ),
    enabled: !!filter.organizationId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
